import React, { Fragment } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  facilitiesService,
  DepartmentContact,
} from "../../../services/facilities.service";
import { DepartmentFormDialog } from "./DepartmentFormDialog";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
  QuickFilter,
} from "../../../../../app.interface";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";
interface SaveDepartmentData {
  _id?: string;
  name: string;
  address?: string;
  contacts?: DepartmentContact[];
}

export const DepartmentsManager: React.FC = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    React.useState<IESDoc | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("name.keyword");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] =
    React.useState<IESDoc | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter departments by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
          description: "Currently active departments",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
          description: "Inactive departments",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort departments by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const quickFilters: QuickFilter[] = [
    {
      id: "active_departments",
      label: "Active Departments",
      icon: "domain",
      color: "success",
      filters: { status: "active" },
    },
    {
      id: "inactive_departments",
      label: "Inactive Departments",
      icon: "domain",
      color: "error",
      filters: { status: "inactive" },
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "departmentId",
      header: "Department ID",
      field: "departmentId",
      type: "action",
      width: "180px",
      renderAction: (row) => {
        return (
          <Fragment>
            <Typography fontWeight={600}>
              {`D-${row.departmentId.substring(0, 9).toUpperCase()}`}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      id: "name",
      header: "Department Name",
      field: "name",
      filterField: "name.keyword",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "contact",
      header: "Department Contact",
      field: "contact",
      type: "contact",
      width: "300px",
      contactName: "contactName",
      contactEmail: "contactEmail",
    },
    {
      id: "address",
      header: "Address",
      field: "address",
      type: "text",
      width: "300px",
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      filterField: "is_deleted",
      type: "status",
      width: "120px",
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: WithId & Record<string, any>) => (
        <div className="flex gap-2 justify-end">
          <Tooltip title="Edit Department" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const originalDepartment = departments.find(
                  (d) => d._id === row._id
                );
                handleOpenFormDialog(originalDepartment);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Department" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              sx={{
                width: 34,
                height: 34,
                bgcolor: (theme) => alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: (theme) => alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    loadDepartments();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await facilitiesService.listDepartments({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setDepartments(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDepartment = async (data: SaveDepartmentData) => {
    try {
      setSaving(true);
      if (selectedDepartment) {
        await facilitiesService.updateDepartment({
          ...data,
          _id: selectedDepartment._id,
          is_active: true,
        });
        toast.success("Department updated successfully");
      } else {
        await facilitiesService.createDepartment({
          ...data,
          is_active: true,
        });
        toast.success("Department created successfully");
      }
      handleCloseFormDialog();
      loadDepartments();
    } catch (error) {
      toast.error("Error saving department");
      console.error("Error saving department:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async () => {
    if (!selectedDepartment) return;
    try {
      setSaving(true);
      await facilitiesService.deleteDepartment(selectedDepartment._id);
      toast.success("Department deleted successfully");
      handleCloseDeleteDialog();
      loadDepartments();
    } catch (error) {
      toast.error("Failed to delete department");
      console.error("Error deleting department:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFormDialog = (department?: IESDoc) => {
    setSelectedDepartment(department || null);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setSelectedDepartment(null);
    setFormDialogOpen(false);
  };

  const handleOpenDeleteDialog = (department: IESDoc) => {
    setSelectedDepartment(department);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedDepartment(null);
    setDeleteDialogOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const department = departments.find((d) => d._id === row._id);
    if (department) {
      setSelectedDepartmentDetails(department);
    }
  };

  const handleDrawerClose = () => {
    setSelectedDepartmentDetails(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Department Management"
        subtitle="Manage your organization's departments"
        icon="domain"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenFormDialog()}
            disabled={
              !hasPermission(
                "inventory.departments.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Department
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search departments by name or address..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={departments.map((department) => ({
            _id: department._id,
            name: department.name,
            address: department.address,
            departmentId: department.department_id,
            createdAt: department.created_at,
            createdBy: department.created_by_email,
            totalCost: department.total_cost || 0,
            status: department.is_deleted ? "inactive" : "active",
            contactName: department.contacts?.[0]?.name || department.name,
            contactEmail: department.contacts?.[0]?.email || "N/A",
          }))}
          allowHeaderWrap
          columns={MasterColumns}
          variant="default"
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          loading={loading}
          header={{
            title: "Departments List",
            subtitle: "Your Current list of departments",
          }}
          selectable
          onSelectionChange={(selected) => {
            console.log("Selected:", selected);
          }}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: () => loadDepartments(),
            },
            export: {
              label: "Export Departments",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "departments-list",
              },
            },
          }}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onRowClick={handleRowClick}
          rowProps={{
            sx: {
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha("#0891B2", 0.04),
              },
            },
          }}
        />
      </Paper>

      {/* Department Details Drawer */}
      <Drawer
        anchor="right"
        open={!!selectedDepartmentDetails}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: "100%",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          },
        }}
      >
        {selectedDepartmentDetails ? (
          <Fragment>
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Icon>domain</Icon>
                <Typography variant="subtitle1" fontWeight={600}>
                  Department Details
                </Typography>
              </Box>
              <IconButton
                onClick={handleDrawerClose}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box display="grid" gap={3}>
                <DetailItem
                  icon="domain"
                  label="Department Name"
                  value={selectedDepartmentDetails.name || "N/A"}
                />
                <DetailItem
                  icon="location_on"
                  label="Address"
                  value={selectedDepartmentDetails.address || "N/A"}
                />
                <DetailItem
                  icon="fiber_manual_record"
                  label="Status"
                  value={
                    selectedDepartmentDetails.is_deleted ? "Inactive" : "Active"
                  }
                  valueColor={
                    selectedDepartmentDetails.is_deleted ? "#DC2626" : "#059669"
                  }
                />
                <DetailItem
                  icon="schedule"
                  label="Created At"
                  value={
                    selectedDepartmentDetails.created_at
                      ? new Date(
                          selectedDepartmentDetails.created_at
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
                <DetailItem
                  icon="person"
                  label="Created By"
                  value={selectedDepartmentDetails.created_by_email || "N/A"}
                />
              </Box>

              {selectedDepartmentDetails.contacts &&
                selectedDepartmentDetails.contacts.length > 0 && (
                  <>
                    <Divider
                      sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }}
                    />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "text.primary",
                        fontFamily: "Montserrat",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>contacts</Icon>
                      Contact Information
                    </Typography>

                    <Box display="grid" gap={3}>
                      {selectedDepartmentDetails.contacts.map(
                        (contact: DepartmentContact, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              bgcolor: alpha("#0891B2", 0.04),
                              border: "1px solid",
                              borderColor: alpha("#0891B2", 0.1),
                            }}
                          >
                            <DetailItem
                              icon="person"
                              label="Contact Name"
                              value={contact.name || "N/A"}
                            />
                            <Box sx={{ mt: 2 }} display="grid" gap={1.5}>
                              {contact.email && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    email
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`mailto:${contact.email}`}
                                  >
                                    {contact.email}
                                  </Typography>
                                </Box>
                              )}
                              {contact.phone && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    phone
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`tel:${contact.phone}`}
                                  >
                                    {contact.phone}
                                  </Typography>
                                </Box>
                              )}
                              {contact.whatsapp && (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Icon
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  >
                                    whatsapp
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0891B2",
                                      fontFamily: "Montserrat",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                    component="a"
                                    href={`https://wa.me/${contact.whatsapp}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {contact.whatsapp}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )
                      )}
                    </Box>
                  </>
                )}

              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

              <Box display="flex" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    handleOpenFormDialog(selectedDepartmentDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.departments.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Edit Department
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Icon>delete</Icon>}
                  onClick={() => {
                    handleOpenDeleteDialog(selectedDepartmentDetails);
                    handleDrawerClose();
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.departments.delete",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                  sx={{
                    borderColor: "#DC2626",
                    color: "#DC2626",
                    "&:hover": {
                      borderColor: "#B91C1C",
                      bgcolor: alpha("#DC2626", 0.04),
                    },
                  }}
                >
                  Delete Department
                </Button>
              </Box>
            </Box>
          </Fragment>
        ) : null}
      </Drawer>

      <DepartmentFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleSaveDepartment}
        department={
          selectedDepartment
            ? {
                _id: selectedDepartment._id,
                name: selectedDepartment.name,
                address: selectedDepartment.address,
                contacts: selectedDepartment.contacts || [],
              }
            : null
        }
        loading={saving}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteDepartment}
        title="Delete Department"
        itemName={selectedDepartment?.name || ""}
        itemDescription={selectedDepartment?.address || ""}
        loading={saving}
        icon="domain"
      />
    </Box>
  );
};

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);
